/* eslint-disable no-unused-vars */
import { Box, Button, Card, Checkbox, Chip, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Step, StepLabel, Stepper, Tooltip, Typography, useTheme, alpha, TextField, InputAdornment, Backdrop, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton } from '@mui/material';
import getStyles from 'src/styles/uploadFileV2Styles';
import React from 'react';
import { sampleCsv } from 'src/constants/commonConstants';
import Papa from 'papaparse';
import { useSelector, useDispatch } from 'react-redux';
import { grey } from 'src/theme/palette';
import { setFileName, setImportFiles, setMappingValues, setOriginalDataFile } from 'src/redux/slices/audienceManager';
import { emailRegex } from 'src/utils/regex';
import { fixAudiencePhone } from 'src/utils/audiencePhone';
import moment from 'moment';
import { isArray, isEmpty } from 'lodash';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import PropTypes from 'prop-types';
import { Iconify } from '..';
import { VisuallyHiddenInput } from '../styledComponents';
import TransferList from './TransferList';

const STEPS = ['Upload a CSV File', 'Select Columns', 'Duplicate Validation', 'Manual Verification', 'Preview & Save'];
const fieldName = [
    { label: 'First name', value: 'firstname' },
    { label: 'Last name', value: 'lastname' },
    { label: 'Title', value: 'title' },
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
    { label: 'Date of Birth', value: 'dob' },
];

// Keyword patterns for auto-assign matching (ordered by priority - most preferred first)
// Note: Patterns are matched against word segments, so 'email' matches 'patient.email' but not 'emailsomething'
// Pattern index affects score: earlier patterns = higher priority
const FIELD_PATTERNS = {
    firstname: ['firstname', 'first_name', 'fname', 'givenname', 'forename'],
    lastname: ['lastname', 'last_name', 'lname', 'surname', 'familyname'],
    title: ['title', 'salutation', 'honorific', 'prefix'],
    email: ['email', 'emailaddress', 'email_address'],
    phone: ['phone', 'mobilephone', 'mobile', 'homephone', 'workphone', 'phonealt', 'telephone', 'cellphone', 'phonenumber', 'phone_number', 'mobilenumber', 'mobile_number'],
    dob: ['dob', 'dateofbirth', 'date_of_birth', 'birthdate', 'birth_date', 'birthday'],
};

const UploadFileV2 = ({ setDisableSave }) => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const fileInputRef = React.useRef(null);
    const { fileName, originalDataFile, importFiles } = useSelector((state) => state.audienceManager);
    const csvLink = fileName;
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();


    const [activeStep, setActiveStep] = React.useState(0);
    const [isFileUploaded, setIsFileUploaded] = React.useState(false);
    const [isFileValue, setIsFileValue] = React.useState('');
    const [csvHeaders, setCsvHeaders] = React.useState([]);
    const [originals, setOriginals] = React.useState([]);
    const [duplicates, setDuplicates] = React.useState([]);
    const [all, setAll] = React.useState([]);
    const [headers, setHeaders] = React.useState([]);
    const [finalHeaders, setFinalHeaders] = React.useState({});
    const [uploadedFile, setUploadedFile] = React.useState([]);
    const [newHeaders, setNewHeaders] = React.useState('');
    const [invalidCsv, setInvalidCsv] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [processingMessage, setProcessingMessage] = React.useState('');

    // Manual Verification state
    const [verificationIssues, setVerificationIssues] = React.useState({
        emptyFirstName: [],
        emptyLastName: [],
        emptyEmail: [],
        invalidPhone: [],
    });
    const [excludedFields, setExcludedFields] = React.useState(new Set());
    const [excludedRecordsByCategory, setExcludedRecordsByCategory] = React.useState({});
    const [editModalOpen, setEditModalOpen] = React.useState(false);
    const [editModalType, setEditModalType] = React.useState(null); // 'emptyFirstName', 'emptyLastName', 'emptyEmail', 'invalidPhone'
    const [editingRecords, setEditingRecords] = React.useState({}); // { recordId: { field: value } }

    // Auto-assign function to match CSV columns to expected fields
    const handleAutoAssign = () => {
        const csvColumns = headers?.[0] || [];
        const newFinalHeaders = { ...finalHeaders };
        const assignedFields = new Set();

        // First, collect already assigned fields
        Object.keys(newFinalHeaders).forEach(headerName => {
            if (newFinalHeaders[headerName]?.visible && newFinalHeaders[headerName]?.mappedToKey) {
                assignedFields.add(newFinalHeaders[headerName].mappedToKey);
            }
        });

        // Score-based matching: higher score = better match
        // Only high-confidence matches (score >= 90) are used
        const getMatchScore = (columnName, patterns) => {
            const normalized = columnName.toLowerCase().replace(/[^a-z0-9]/g, '');
            const lowerColumn = columnName.toLowerCase();
            // Split by non-alphanumeric to get word segments (e.g., "patient.firstname" -> ["patient", "firstname"])
            const segments = lowerColumn.split(/[^a-z0-9]+/).filter(Boolean);

            // Bonus for patient-related columns (this is a patient CRM)
            const patientBonus = segments.includes('patient') ? 10 : 0;

            for (let i = 0; i < patterns.length; i += 1) {
                const pattern = patterns[i].replace(/[^a-z0-9]/g, '');

                // Exact match of entire column name (highest priority)
                if (normalized === pattern) return 100 - i + patientBonus;

                // Pattern matches a complete segment (e.g., "firstname" in "patient.firstname")
                if (segments.includes(pattern)) return 95 - i + patientBonus;

                // Pattern matches the last segment exactly (common for nested fields like "patient.firstname")
                if (segments.length > 0 && segments[segments.length - 1] === pattern) return 92 - i + patientBonus;
            }
            // Return 0 for partial/embedded matches - too risky for auto-assign
            return 0;
        };

        // Build matches with scores
        const matches = [];
        csvColumns.forEach(columnName => {
            Object.entries(FIELD_PATTERNS).forEach(([fieldKey, patterns]) => {
                const score = getMatchScore(columnName, patterns);
                if (score > 0) {
                    matches.push({ columnName, fieldKey, score });
                }
            });
        });

        // Sort by score descending and assign uniquely
        matches.sort((a, b) => b.score - a.score);

        matches.forEach(({ columnName, fieldKey }) => {
            if (!assignedFields.has(fieldKey)) {
                // Check if this column is already assigned to something else
                const existingMapping = newFinalHeaders[columnName]?.mappedToKey;
                if (!existingMapping || !assignedFields.has(existingMapping)) {
                    newFinalHeaders[columnName] = {
                        visible: true,
                        mappedToKey: fieldKey
                    };
                    assignedFields.add(fieldKey);
                }
            }
        });

        setFinalHeaders(newFinalHeaders);
        enqueueSnackbar(`Auto-assigned ${assignedFields.size} columns`, {
            variant: 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
        });
    };

    // Clear all mappings
    const handleClearAll = () => {
        setFinalHeaders({});
    };

    // Get mapped columns count
    const getMappedCount = () => Object.keys(finalHeaders).filter(key => finalHeaders[key]?.visible).length;

    // Filter and sort columns: mapped first, then alphabetically within each group
    const getFilteredHeaders = () => {
        let allHeaders = headers?.[0] || [];

        // Filter by search term if provided
        if (searchTerm) {
            allHeaders = allHeaders.filter(header =>
                header.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort: mapped columns first, then unmapped, alphabetically within each group
        return allHeaders.sort((a, b) => {
            const aIsMapped = finalHeaders[a]?.visible ? 1 : 0;
            const bIsMapped = finalHeaders[b]?.visible ? 1 : 0;

            // If different mapped status, mapped comes first
            if (aIsMapped !== bIsMapped) {
                return bIsMapped - aIsMapped;
            }

            // Same mapped status, sort alphabetically
            return a.toLowerCase().localeCompare(b.toLowerCase());
        });
    };

    const handleHeaders = (file, newHeaders) => {
        Papa.parse(file, {
            download: true,
            header: false,
            complete(results) {
                if (newHeaders) {
                    setNewHeaders(results.data);
                } else {
                    setHeaders(results.data);
                }
            },
        });
    };


    const convertToBase64 = (result, isOriginal) => {
        const reader = new FileReader();
        reader.readAsDataURL(result);
        reader.onload = () => {
            if (isOriginal) {
                dispatch(setOriginalDataFile(reader.result));
                handleHeaders(result, true);
                return;
            }
            dispatch(setImportFiles(reader.result));
        };
    };

    // Shared phone rule, loaded on demand so libphonenumber-js stays out of the first-load bundle.
    const phoneLibRef = React.useRef(null);
    const loadPhoneLib = React.useCallback(async () => {
        if (!phoneLibRef.current) phoneLibRef.current = await import('src/utils/phoneCheck');
        return phoneLibRef.current;
    }, []);
    React.useEffect(() => { loadPhoneLib().catch(() => {}); }, [loadPhoneLib]);

    // Phone check = the shared lead-phone rule (src/utils/phoneCheck), the same answer
    // the desk-phone dial and the backend give (CU-869fnwgxf). It replaced a local
    // mobile-only regex that guessed country codes (44/971/966/91). Safe clean-ups only
    // (spaces, punctuation, 00 → +); never adds or removes a digit. The library loads
    // on demand (phoneLibRef); handleNext waits for it before the first check runs.
    function validateAndFixWhatsAppNumber(input) {
        return fixAudiencePhone(input, phoneLibRef.current);
    }


    const convertToCSVInvalid = fileData => {
        if (isEmpty(fileData)) {
            setInvalidCsv(null);
            return;
        }
        const newHeaders = Object.keys(finalHeaders)?.filter(el => finalHeaders?.[el]?.visible)?.map(el => finalHeaders[el]?.mappedToKey);
        const data = fileData.map((el) => {
            let payload = {};
            if (isArray(newHeaders) && newHeaders.length > 0) {
                payload = newHeaders.reduce((total, current) => {
                    total = { ...total, [current]: el[current] };
                    return total;
                }, {});
            } else {
                payload = {
                    firstname: el.firstname || '',
                    lastname: el.lastname || '',
                    email: el?.email || '',
                    phone: el?.phone || '',
                    salutation: el?.salutation || '',
                    dob: el.dob || '',
                };
            }
            return payload;
        });
        const csvRows = [];
        const keys = Object.keys(data[0]);
        csvRows.push(keys.join(','));
        // eslint-disable-next-line no-restricted-syntax
        for (const row of data) {
            const values = keys.map((key) => `"${row[key]}"`);
            csvRows.push(values.join(','));
        }
        const newCsvData = csvRows.join('\n');

        const blob = new Blob([newCsvData], { type: 'text/csv;charset=utf-8;' });

        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => {
            setInvalidCsv(reader.result);
        };
    }

    const convertToCSV = (fileData) => {
        if (isEmpty(fileData)) {
            dispatch(setImportFiles(''));
            return;
        }
        const newHeaders = Object.keys(finalHeaders)?.filter(el => finalHeaders?.[el]?.visible)?.map(el => finalHeaders[el]?.mappedToKey);
        const data = fileData.map((el) => {
            let payload = {};
            if (isArray(newHeaders) && newHeaders.length > 0) {
                payload = newHeaders.reduce((total, current) => {
                    total = { ...total, [current]: el[current] };
                    return total;
                }, {});
            } else {
                payload = {
                    firstname: el.firstname || '',
                    lastname: el.lastname || '',
                    email: el?.email || '',
                    phone: el?.phone || '',
                    salutation: el?.salutation || '',
                    dob: el.dob || '',
                };
            }
            return payload;
        });
        const csvRows = [];
        const keys = Object.keys(data[0]);
        csvRows.push(keys.join(','));
        // eslint-disable-next-line no-restricted-syntax
        for (const row of data) {
            const values = keys.map((key) => `"${row[key]}"`);
            csvRows.push(values.join(','));
        }
        const newCsvData = csvRows.join('\n');

        const blob = new Blob([newCsvData], { type: 'text/csv;charset=utf-8;' });
        convertToBase64(blob, true);
    };

    // Detect verification issues in the data
    const detectVerificationIssues = (data) => {
        const issues = {
            emptyFirstName: [],
            emptyLastName: [],
            emptyEmail: [],
            invalidPhone: [],
        };

        data?.forEach((record) => {
            // Check for empty first name
            if (!record?.firstname || record.firstname.trim() === '') {
                issues.emptyFirstName.push(record);
            }
            // Check for empty last name
            if (!record?.lastname || record.lastname.trim() === '') {
                issues.emptyLastName.push(record);
            }
            // Check for empty email
            if (!record?.email || record.email.trim() === '') {
                issues.emptyEmail.push(record);
            }
            // Check for empty or invalid phone
            if (!record?.phone || record.phone.trim() === '') {
                issues.invalidPhone.push(record);
            } else {
                const phoneCheck = validateAndFixWhatsAppNumber(record.phone);
                if (!phoneCheck.valid) {
                    issues.invalidPhone.push(record);
                }
            }
        });

        return issues;
    };

    // Open edit modal for a specific issue type
    const handleOpenEditModal = (issueType) => {
        setEditModalType(issueType);
        // Initialize editing records with current values
        const records = verificationIssues[issueType] || [];
        const initialEdits = {};
        records.forEach(record => {
            initialEdits[record.id] = {
                firstname: record.firstname || '',
                lastname: record.lastname || '',
                email: record.email || '',
                phone: record.phone || '',
            };
        });
        setEditingRecords(initialEdits);
        setEditModalOpen(true);
    };

    // Close edit modal
    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setEditModalType(null);
        setEditingRecords({});
    };

    // Update a field value for a specific record in the modal
    const handleEditFieldChange = (recordId, field, value) => {
        setEditingRecords(prev => ({
            ...prev,
            [recordId]: {
                ...prev[recordId],
                [field]: value
            }
        }));
    };

    // Save all changes from the modal
    const handleSaveAllEdits = () => {
        const updatedOriginals = originals.map(record => {
            if (editingRecords[record.id]) {
                return { ...record, ...editingRecords[record.id] };
            }
            return record;
        });

        setOriginals(updatedOriginals);
        convertToCSV(updatedOriginals);

        // Re-detect issues
        setVerificationIssues(detectVerificationIssues(updatedOriginals));
        handleCloseEditModal();

        enqueueSnackbar('Records updated successfully', {
            variant: 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
        });
    };

    // Handle excluding a field type - removes records from final copy
    const handleExcludeField = (fieldType) => {
        const isCurrentlyExcluded = excludedFields.has(fieldType);

        if (isCurrentlyExcluded) {
            // Re-include: restore excluded records back to originals
            const recordsToRestore = excludedRecordsByCategory[fieldType] || [];
            if (recordsToRestore.length > 0) {
                const updatedOriginals = [...originals, ...recordsToRestore];
                const updatedDuplicates = duplicates.filter(
                    d => !recordsToRestore.some(r => r.id === d.id)
                );
                setOriginals(updatedOriginals);
                setDuplicates(updatedDuplicates);
                convertToCSV(updatedOriginals);
                setVerificationIssues(detectVerificationIssues(updatedOriginals));
            }

            // Remove from excluded tracking
            setExcludedRecordsByCategory(prev => {
                const newState = { ...prev };
                delete newState[fieldType];
                return newState;
            });
            setExcludedFields(prev => {
                const newExcluded = new Set(prev);
                newExcluded.delete(fieldType);
                return newExcluded;
            });

            enqueueSnackbar(`Records with ${fieldType.replace(/([A-Z])/g, ' $1').toLowerCase()} included back`, {
                variant: 'success',
                anchorOrigin: { vertical: 'top', horizontal: 'center' }
            });
        } else {
            // Exclude: move records with this issue to duplicates
            const recordsToExclude = verificationIssues[fieldType] || [];
            if (recordsToExclude.length > 0) {
                const recordIdsToExclude = new Set(recordsToExclude.map(r => r.id));
                const updatedOriginals = originals.filter(r => !recordIdsToExclude.has(r.id));
                const updatedDuplicates = [...duplicates, ...recordsToExclude];

                setOriginals(updatedOriginals);
                setDuplicates(updatedDuplicates);
                convertToCSV(updatedOriginals);

                // Store excluded records for potential restoration
                setExcludedRecordsByCategory(prev => ({
                    ...prev,
                    [fieldType]: recordsToExclude
                }));

                // Update verification issues for remaining records
                setVerificationIssues(detectVerificationIssues(updatedOriginals));
            }

            setExcludedFields(prev => {
                const newExcluded = new Set(prev);
                newExcluded.add(fieldType);
                return newExcluded;
            });

            enqueueSnackbar(`${recordsToExclude.length} records excluded from import`, {
                variant: 'info',
                anchorOrigin: { vertical: 'top', horizontal: 'center' }
            });
        }
    };

    // Handle removing a record from originals (move to duplicates/rejected)
    const handleRemoveRecord = (record) => {
        const updatedOriginals = originals.filter(r => r.id !== record.id);
        const updatedDuplicates = [...duplicates, record];

        setOriginals(updatedOriginals);
        setDuplicates(updatedDuplicates);
        convertToCSV(updatedOriginals);

        // Re-detect issues
        setVerificationIssues(detectVerificationIssues(updatedOriginals));

        enqueueSnackbar('Record moved to rejected list', {
            variant: 'info',
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
        });
    };

    const dobCheck = (item) => {
        if (!item?.dob) return true;
        const dob = moment(item?.dob, 'DD/MM/YYYY', true);
        if (!dob.isValid()) return true;
        return moment().diff(dob, 'years') > 17;
    };

    const handleFilter = (group, key) => group?.find((item) => item?.salutation && item?.salutation?.toLowerCase() === key && dobCheck(item));

    const checkIfEmailFieldEnabled = (headers) => Object.keys(headers ?? {})?.some(
        key => headers[key]?.mappedToKey === "email" && headers[key]?.visible
    ) ?? false;

    const checkIfPhoneFieldEnabled = (headers) => Object.keys(headers ?? {})?.some(
        key => headers[key]?.mappedToKey === "phone" && headers[key]?.visible
    ) ?? false;

    const processDataAsync = () => {
        const newMappings = {};
        const invalidEntries = [];
        Object.keys(finalHeaders)?.forEach(headerName => {
            if (finalHeaders?.[headerName]?.visible)
                newMappings[headerName] = finalHeaders?.[headerName]?.mappedToKey;
        })

        dispatch(setMappingValues({ firstname: "lead_first_name", lastname: "lead_last_name", title: "lead_salutation", email: "lead_email", phone: "lead_phone" }));

        // filter new data
        const newData = uploadedFile?.map(eachData => {
            const newObj = {};
            Object.keys(eachData)?.forEach(eachKey => {
                if (finalHeaders[eachKey]?.visible) {
                    newObj[finalHeaders[eachKey]?.mappedToKey || eachKey] = eachData?.[eachKey];
                }
            })
            return newObj;
        });

        if (newData?.length) {
            setProcessingMessage(`Validating ${newData.length} records...`);
            const validEntries = [];
            newData.forEach(entry => {
                try {
                    if (!entry?.email && checkIfEmailFieldEnabled(finalHeaders)) throw new Error("Invalid Email");
                    let emailList = entry?.email?.split(/\s+/);
                    emailList = emailList?.filter(em => emailRegex.test(em));
                    if (!(emailList?.[0]?.length) && checkIfEmailFieldEnabled(finalHeaders)) throw new Error("Invalid Email");
                    const phoneObject = validateAndFixWhatsAppNumber(entry?.phone);
                    if (phoneObject?.valid) {
                        entry.phone = phoneObject?.fixedNumber
                    } else if (checkIfPhoneFieldEnabled(finalHeaders)) {
                        throw new Error("Invalid Phone number")
                    }
                    validEntries.push(entry);
                } catch (_) {
                    invalidEntries.push(entry);
                }
            })

            setProcessingMessage(`Processing ${validEntries.length} valid records...`);
            const filteredData = validEntries?.map((entry, index) => {
                let emailList = entry?.email?.split(/\s+/);
                emailList = emailList?.filter(em => emailRegex.test(em));
                const email = checkIfEmailFieldEnabled(finalHeaders) ? "" : `${entry?.firstname}-${entry?.lastname}-${entry?.dob}-${entry?.index}`
                return { ...entry, email: emailList?.[0] ?? email }
            });

            setProcessingMessage('Detecting duplicates...');
            const groupByEmail = filteredData.reduce((groups, item) => {
                const group = groups[item?.email] || [];
                group.push(item);
                groups[item?.email] = group;
                return groups;
            }, {});
            const originals = [];
            const duplicates = [];

            // Use Set for O(1) lookups instead of JSON.stringify comparisons
            const originalSet = new Set();
            const getItemKey = (item) => JSON.stringify(item);

            Object.values(groupByEmail).forEach((group) => {
                if (group.length > 1) {
                    let original = handleFilter(group, 'mrs');
                    if (!original) {
                        original = handleFilter(group, 'mr');;
                    }
                    if (!original) {
                        const filteredData = group?.find((item) => item?.salutation && dobCheck(item));
                        if (!isEmpty(filteredData)) {
                            original = filteredData;
                        }
                    }
                    if (isEmpty(original)) {
                        const filteredData = group?.find((item) => dobCheck(item));
                        if (!isEmpty(filteredData)) {
                            original = filteredData;
                        }
                    }
                    if (isArray(original)) {
                        original.forEach(o => {
                            originals.push(o);
                            originalSet.add(getItemKey(o));
                        });
                        const duplicateItems = group.filter((item) => !originalSet.has(getItemKey(item)));
                        duplicates.push(...duplicateItems);
                    } else {
                        const originalKey = getItemKey(original);
                        originals.push(original);
                        originalSet.add(originalKey);
                        const duplicateItems = group.filter((item) => getItemKey(item) !== originalKey);
                        duplicates.push(...duplicateItems);
                    }
                } else {
                    const item = group[0];
                    if (dobCheck(item)) {
                        originals.push(item);
                        originalSet.add(getItemKey(item));
                    } else {
                        duplicates.push(item);
                    }
                }
            });

            setProcessingMessage('Finalizing...');
            const filteredOriginals = originals?.filter((ele) => ele?.email) || [];
            const filteredOriginalsSet = new Set(filteredOriginals.map(getItemKey));
            const filteredDuplicates = filteredData.filter((item) => !filteredOriginalsSet.has(getItemKey(item))) || [];

            setOriginals(filteredOriginals?.map((ele, index) => ({ ...ele, id: index })));
            convertToCSV(filteredOriginals);
            convertToCSVInvalid(invalidEntries);
            setDuplicates(filteredDuplicates?.map((ele, index) => ({ ...ele, id: originals?.length + index })));
            setAll([...filteredOriginals, ...filteredDuplicates]?.map((ele, index) => ({ ...ele, id: index })));
        }
    };

    const handleNext = () => {
        if (activeStep === 1) {
            // Show loading and process asynchronously to prevent UI freeze
            setIsProcessing(true);
            setProcessingMessage('Preparing data...');

            // Use setTimeout to allow UI to update before heavy processing
            setTimeout(async () => {
                try {
                    await loadPhoneLib();
                    processDataAsync();
                } catch (error) {
                    console.error('processDataAsync error:', error);
                    console.error('Error stack:', error?.stack);
                    enqueueSnackbar('Error processing data. Please try again.', {
                        variant: 'error',
                        anchorOrigin: { vertical: 'top', horizontal: 'center' }
                    });
                } finally {
                    setIsProcessing(false);
                    setProcessingMessage('');
                    setActiveStep((prevActiveStep) => prevActiveStep + 1);
                }
            }, 100);
            return;
        }
        if (activeStep === 2 && invalidCsv) {
            enqueueSnackbar(`Please preview the invalidated data and re-upload if needed!`, {
                variant: 'warning',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
        }
        // Detect verification issues when moving to Manual Verification step
        if (activeStep === 2) {
            const issues = detectVerificationIssues(originals);
            setVerificationIssues(issues);
            setExcludedFields(new Set());
            setExcludedRecordsByCategory({});
            setEditModalOpen(false);
            setEditModalType(null);
            setEditingRecords({});
        }
        // Enable save on final step
        if (activeStep === 3) {
            setDisableSave(false);
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const isNextDisabled = currStep => {
        switch (currStep) {
            case 0: return false;
            default: return false;
        }
    }

    const handleBack = () => {
        setDisableSave(true)
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleFileUpload = (event) => {
        if (event.target.value) {
            setCsvHeaders([]);
            setFinalHeaders({});
            dispatch(setMappingValues({}));
            dispatch(setImportFiles(''));
            dispatch(setOriginalDataFile(''));
            setIsFileUploaded(true);
            setIsFileValue(event.target.files[0].name);
            dispatch(setFileName(event.target.files[0].name));
            convertToBase64(event.target.files[0]);
            Papa.parse(event.target.files[0], {
                complete(results) {
                    const filteredData = results.data?.filter((item) => !Object.values(item).every(value => value === '')) || [];
                    setUploadedFile(filteredData);
                    fileInputRef.current.value = null;
                    handleNext();
                },
                header: true,
            });
            handleHeaders(event.target.files[0]);
        }
    };

    React.useEffect(() => {
        if (importFiles) {
            setIsFileValue(fileName);
            setIsFileUploaded(true);
            handleHeaders(importFiles);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importFiles]);

    return (
        <>
            {/* Loading Overlay */}
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    flexDirection: 'column',
                    gap: 2
                }}
                open={isProcessing}
            >
                <CircularProgress color="inherit" size={48} />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    {processingMessage || 'Processing...'}
                </Typography>
            </Backdrop>

            <Box>
                <Card sx={styles.root}>
                    {/** Stepper Component */}
                    <Box sx={styles.stepperBox}>
                        <Stepper activeStep={activeStep}>
                            {STEPS.map((label) => {
                                const stepProps = {};
                                const labelProps = {};
                                return (
                                    <Step key={label} {...stepProps}>
                                        <StepLabel {...labelProps}>{label}</StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </Box>
                    {/** Stepper Step Component */}
                    <Box sx={styles.stepperStepComponent}>
                        {activeStep === 0 && (
                            <Box sx={styles.dropBox}>
                                <Box sx={{ mb: 1 }}>
                                    <Button component='label' variant='contained'
                                        sx={{ borderRadius: "0.5rem" }}>
                                        Upload File
                                        <VisuallyHiddenInput
                                            type='file'
                                            onChange={handleFileUpload}
                                            accept='.csv'
                                            ref={fileInputRef} />
                                    </Button>
                                </Box>
                                {isFileValue && (
                                    <Typography
                                        sx={{
                                            fontWeight: '700', display: 'block',
                                            color: grey[600]
                                        }}
                                        component={isFileValue?.split('csv/')[1] ? 'a' : 'p'}
                                        href={csvLink}
                                        clickable
                                    >
                                        {isFileValue.split('csv/')[1] || isFileValue || ''}
                                    </Typography>
                                )}
                                <Box>
                                    <Typography sx={{ fontSize: '14px', color: grey[600] }}>You can import customer data here with a .csv file</Typography>
                                </Box>
                                <Tooltip title="DownloadSample CSV File">
                                    <Button href={sampleCsv} sx={{ ml: 1 }} endIcon={<Iconify
                                        sx={{ cursor: 'pointer' }}
                                        icon={'material-symbols:download-rounded'}
                                        width={14}
                                        height={14}
                                    />}>
                                        <Typography sx={{ fontSize: '10px', color: grey[600] }}>Click here to download sample file</Typography>
                                    </Button>
                                </Tooltip>
                            </Box>)}
                        {/** Active step for Selecting Headers */}
                        {activeStep === 1 && (
                            <Box sx={{ width: '100%', p: 2 }}>
                                {/* Header Section with controls */}
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    mb: 3,
                                    pb: 2,
                                    borderBottom: `1px solid ${theme.palette.divider}`
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            CSV Columns
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={`${headers?.[0]?.length || 0} total`}
                                            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                                        />
                                        <Chip
                                            size="small"
                                            label={`${getMappedCount()} mapped`}
                                            color="success"
                                            variant={getMappedCount() > 0 ? 'filled' : 'outlined'}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder="Search columns..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            sx={{ width: 200 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Tooltip title="Automatically detect and map columns based on their names">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<Iconify icon="mdi:auto-fix" width={18} />}
                                                onClick={handleAutoAssign}
                                                sx={{
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    bgcolor: theme.palette.success.main,
                                                    '&:hover': { bgcolor: theme.palette.success.dark }
                                                }}
                                            >
                                                Auto Assign
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Clear all mappings">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="error"
                                                onClick={handleClearAll}
                                                sx={{ borderRadius: '8px', textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                                            >
                                                <Iconify icon="mdi:close-circle-outline" width={18} />
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Columns Grid */}
                                <Box sx={{
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    pr: 1,
                                    '&::-webkit-scrollbar': { width: '6px' },
                                    '&::-webkit-scrollbar-thumb': {
                                        bgcolor: alpha(theme.palette.grey[500], 0.3),
                                        borderRadius: '3px'
                                    }
                                }}>
                                    <Grid container spacing={1.5}>
                                        {getFilteredHeaders()?.map((headerName, index) => {
                                            const isSelected = finalHeaders[headerName]?.visible;
                                            const mappedTo = finalHeaders[headerName]?.mappedToKey;
                                            const mappedLabel = fieldName.find(f => f.value === mappedTo)?.label;

                                            return (
                                                <Grid item xs={12} sm={6} md={4} key={index}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            p: 1.5,
                                                            borderRadius: '10px',
                                                            border: `1px solid ${isSelected
                                                                ? theme.palette.primary.main
                                                                : theme.palette.divider}`,
                                                            bgcolor: isSelected
                                                                ? alpha(theme.palette.primary.main, 0.04)
                                                                : 'background.paper',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                borderColor: theme.palette.primary.light,
                                                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: isSelected ? 1 : 0 }}>
                                                            <Checkbox
                                                                checked={isSelected || false}
                                                                onChange={(event) => {
                                                                    setFinalHeaders(prvObj => ({
                                                                        ...prvObj,
                                                                        [headerName]: {
                                                                            ...prvObj?.[headerName],
                                                                            visible: event.target.checked
                                                                        }
                                                                    }));
                                                                }}
                                                                size="small"
                                                                sx={{ p: 0.5, mr: 1 }}
                                                            />
                                                            <Tooltip title={headerName}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: isSelected ? 600 : 400,
                                                                        color: isSelected ? 'text.primary' : 'text.secondary',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        flex: 1
                                                                    }}
                                                                >
                                                                    {headerName}
                                                                </Typography>
                                                            </Tooltip>
                                                        </Box>

                                                        {isSelected && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3.5 }}>
                                                                <Iconify
                                                                    icon="heroicons:arrow-right-20-solid"
                                                                    width={16}
                                                                    sx={{ color: theme.palette.primary.main }}
                                                                />
                                                                <FormControl fullWidth size="small">
                                                                    <Select
                                                                        value={mappedTo || ''}
                                                                        displayEmpty
                                                                        sx={{
                                                                            height: '32px',
                                                                            fontSize: '0.8rem',
                                                                            '& .MuiSelect-select': {
                                                                                py: 0.75
                                                                            }
                                                                        }}
                                                                        onChange={(e) => {
                                                                            setFinalHeaders(prvObj => ({
                                                                                ...prvObj,
                                                                                [headerName]: {
                                                                                    ...prvObj?.[headerName],
                                                                                    mappedToKey: e.target.value
                                                                                }
                                                                            }));
                                                                        }}
                                                                    >
                                                                        <MenuItem value="" disabled>
                                                                            <em>Select mapping...</em>
                                                                        </MenuItem>
                                                                        {fieldName?.map((fn, idx) => (
                                                                            <MenuItem key={idx} value={fn?.value}>
                                                                                {fn?.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>

                                {/* Legend */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3,
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${theme.palette.divider}`
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Legend:
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '3px',
                                            border: `1px solid ${theme.palette.divider}`,
                                            bgcolor: 'background.paper'
                                        }} />
                                        <Typography variant="caption" color="text.secondary">Unmapped</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '3px',
                                            border: `1px solid ${theme.palette.primary.main}`,
                                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                                        }} />
                                        <Typography variant="caption" color="text.secondary">Mapped</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        {/** Active step for selecting duplicates */}
                        {activeStep === 2 && (<Box sx={styles.transferList}>
                            <TransferList
                                originalProps={{ originals, setOriginals }}
                                duplicateProps={{ duplicates, setDuplicates }}
                                allProps={{ all, setAll }}
                                convertToCSV={convertToCSV}
                                headers={newHeaders}
                            />
                        </Box>)}
                        {/** Manual Verification Step */}
                        {activeStep === 3 && (
                            <Box sx={{ width: '100%', p: 2 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Data Quality Verification
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Review and fix records with potential issues before finalizing your import.
                                    Total valid records: <strong>{originals?.length || 0}</strong>
                                </Typography>

                                <Grid container spacing={2}>
                                    {/* Empty First Name Card */}
                                    <Grid item xs={12} md={6}>
                                        <Card
                                            sx={{
                                                p: 2,
                                                border: `1px solid ${excludedFields.has('emptyFirstName') ? theme.palette.grey[300] : theme.palette.warning.light}`,
                                                bgcolor: excludedFields.has('emptyFirstName') ? alpha(theme.palette.grey[500], 0.05) : alpha(theme.palette.warning.main, 0.05),
                                                opacity: excludedFields.has('emptyFirstName') ? 0.7 : 1
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Iconify icon="mdi:account-alert" width={24} color={theme.palette.warning.main} />
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        Empty First Name
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={excludedFields.has('emptyFirstName') ? (excludedRecordsByCategory.emptyFirstName?.length || 0) : verificationIssues.emptyFirstName.length}
                                                    color={excludedFields.has('emptyFirstName') || verificationIssues.emptyFirstName.length === 0 ? 'default' : 'warning'}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {excludedFields.has('emptyFirstName')
                                                    ? `${excludedRecordsByCategory.emptyFirstName?.length || 0} records excluded`
                                                    : `${verificationIssues.emptyFirstName.length} records have missing first name`}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant={excludedFields.has('emptyFirstName') ? 'contained' : 'outlined'}
                                                    color={excludedFields.has('emptyFirstName') ? 'primary' : 'error'}
                                                    onClick={() => handleExcludeField('emptyFirstName')}
                                                    startIcon={<Iconify icon={excludedFields.has('emptyFirstName') ? 'mdi:plus-circle-outline' : 'mdi:minus-circle-outline'} />}
                                                >
                                                    {excludedFields.has('emptyFirstName') ? 'Include' : 'Exclude'}
                                                </Button>
                                                {verificationIssues.emptyFirstName.length > 0 && !excludedFields.has('emptyFirstName') && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => handleOpenEditModal('emptyFirstName')}
                                                        startIcon={<Iconify icon="mdi:pencil" />}
                                                    >
                                                        Edit Records
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>

                                    {/* Empty Last Name Card */}
                                    <Grid item xs={12} md={6}>
                                        <Card
                                            sx={{
                                                p: 2,
                                                border: `1px solid ${excludedFields.has('emptyLastName') ? theme.palette.grey[300] : theme.palette.warning.light}`,
                                                bgcolor: excludedFields.has('emptyLastName') ? alpha(theme.palette.grey[500], 0.05) : alpha(theme.palette.warning.main, 0.05),
                                                opacity: excludedFields.has('emptyLastName') ? 0.7 : 1
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Iconify icon="mdi:account-alert" width={24} color={theme.palette.warning.main} />
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        Empty Last Name
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={excludedFields.has('emptyLastName') ? (excludedRecordsByCategory.emptyLastName?.length || 0) : verificationIssues.emptyLastName.length}
                                                    color={excludedFields.has('emptyLastName') || verificationIssues.emptyLastName.length === 0 ? 'default' : 'warning'}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {excludedFields.has('emptyLastName')
                                                    ? `${excludedRecordsByCategory.emptyLastName?.length || 0} records excluded`
                                                    : `${verificationIssues.emptyLastName.length} records have missing last name`}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant={excludedFields.has('emptyLastName') ? 'contained' : 'outlined'}
                                                    color={excludedFields.has('emptyLastName') ? 'primary' : 'error'}
                                                    onClick={() => handleExcludeField('emptyLastName')}
                                                    startIcon={<Iconify icon={excludedFields.has('emptyLastName') ? 'mdi:plus-circle-outline' : 'mdi:minus-circle-outline'} />}
                                                >
                                                    {excludedFields.has('emptyLastName') ? 'Include' : 'Exclude'}
                                                </Button>
                                                {verificationIssues.emptyLastName.length > 0 && !excludedFields.has('emptyLastName') && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => handleOpenEditModal('emptyLastName')}
                                                        startIcon={<Iconify icon="mdi:pencil" />}
                                                    >
                                                        Edit Records
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>

                                    {/* Empty Email Card */}
                                    <Grid item xs={12} md={6}>
                                        <Card
                                            sx={{
                                                p: 2,
                                                border: `1px solid ${excludedFields.has('emptyEmail') ? theme.palette.grey[300] : theme.palette.error.light}`,
                                                bgcolor: excludedFields.has('emptyEmail') ? alpha(theme.palette.grey[500], 0.05) : alpha(theme.palette.error.main, 0.05),
                                                opacity: excludedFields.has('emptyEmail') ? 0.7 : 1
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Iconify icon="mdi:email-alert" width={24} color={theme.palette.error.main} />
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        Empty Email
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={excludedFields.has('emptyEmail') ? (excludedRecordsByCategory.emptyEmail?.length || 0) : verificationIssues.emptyEmail.length}
                                                    color={excludedFields.has('emptyEmail') || verificationIssues.emptyEmail.length === 0 ? 'default' : 'error'}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {excludedFields.has('emptyEmail')
                                                    ? `${excludedRecordsByCategory.emptyEmail?.length || 0} records excluded`
                                                    : `${verificationIssues.emptyEmail.length} records have missing email address`}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant={excludedFields.has('emptyEmail') ? 'contained' : 'outlined'}
                                                    color={excludedFields.has('emptyEmail') ? 'primary' : 'error'}
                                                    onClick={() => handleExcludeField('emptyEmail')}
                                                    startIcon={<Iconify icon={excludedFields.has('emptyEmail') ? 'mdi:plus-circle-outline' : 'mdi:minus-circle-outline'} />}
                                                >
                                                    {excludedFields.has('emptyEmail') ? 'Include' : 'Exclude'}
                                                </Button>
                                                {verificationIssues.emptyEmail.length > 0 && !excludedFields.has('emptyEmail') && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => handleOpenEditModal('emptyEmail')}
                                                        startIcon={<Iconify icon="mdi:pencil" />}
                                                    >
                                                        Edit Records
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>

                                    {/* Invalid Phone Card */}
                                    <Grid item xs={12} md={6}>
                                        <Card
                                            sx={{
                                                p: 2,
                                                border: `1px solid ${excludedFields.has('invalidPhone') ? theme.palette.grey[300] : theme.palette.error.light}`,
                                                bgcolor: excludedFields.has('invalidPhone') ? alpha(theme.palette.grey[500], 0.05) : alpha(theme.palette.error.main, 0.05),
                                                opacity: excludedFields.has('invalidPhone') ? 0.7 : 1
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Iconify icon="mdi:phone-alert" width={24} color={theme.palette.error.main} />
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        Empty/Invalid Phone
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={excludedFields.has('invalidPhone') ? (excludedRecordsByCategory.invalidPhone?.length || 0) : verificationIssues.invalidPhone.length}
                                                    color={excludedFields.has('invalidPhone') || verificationIssues.invalidPhone.length === 0 ? 'default' : 'error'}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {excludedFields.has('invalidPhone')
                                                    ? `${excludedRecordsByCategory.invalidPhone?.length || 0} records excluded`
                                                    : `${verificationIssues.invalidPhone.length} records have missing or invalid phone number`}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant={excludedFields.has('invalidPhone') ? 'contained' : 'outlined'}
                                                    color={excludedFields.has('invalidPhone') ? 'primary' : 'error'}
                                                    onClick={() => handleExcludeField('invalidPhone')}
                                                    startIcon={<Iconify icon={excludedFields.has('invalidPhone') ? 'mdi:plus-circle-outline' : 'mdi:minus-circle-outline'} />}
                                                >
                                                    {excludedFields.has('invalidPhone') ? 'Include' : 'Exclude'}
                                                </Button>
                                                {verificationIssues.invalidPhone.length > 0 && !excludedFields.has('invalidPhone') && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => handleOpenEditModal('invalidPhone')}
                                                        startIcon={<Iconify icon="mdi:pencil" />}
                                                    >
                                                        Edit Records
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>
                                </Grid>

                                {/* Summary */}
                                <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Summary:</strong> You can either fix individual records or exclude entire issue categories.
                                        Excluded records will be removed from the final import.
                                    </Typography>
                                </Box>

                                {/* Edit Records Modal */}
                                <Dialog
                                    open={editModalOpen}
                                    onClose={handleCloseEditModal}
                                    maxWidth="lg"
                                    fullWidth
                                    PaperProps={{ sx: { maxHeight: '80vh' } }}
                                >
                                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="h6">
                                            Edit Records - {editModalType === 'emptyFirstName' && 'Empty First Name'}
                                            {editModalType === 'emptyLastName' && 'Empty Last Name'}
                                            {editModalType === 'emptyEmail' && 'Empty Email'}
                                            {editModalType === 'invalidPhone' && 'Empty/Invalid Phone'}
                                        </Typography>
                                        <IconButton onClick={handleCloseEditModal} size="small">
                                            <Iconify icon="mdi:close" />
                                        </IconButton>
                                    </DialogTitle>
                                    <DialogContent dividers>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>First Name</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Last Name</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Email</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Phone</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, width: 80 }}>Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {editModalType && verificationIssues[editModalType]?.map((record) => (
                                                        <TableRow key={record.id} hover>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    value={editingRecords[record.id]?.firstname || ''}
                                                                    onChange={(e) => handleEditFieldChange(record.id, 'firstname', e.target.value)}
                                                                    placeholder="Enter first name"
                                                                    error={editModalType === 'emptyFirstName' && !editingRecords[record.id]?.firstname}
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            bgcolor: editModalType === 'emptyFirstName' ? alpha(theme.palette.warning.main, 0.1) : 'transparent'
                                                                        }
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    value={editingRecords[record.id]?.lastname || ''}
                                                                    onChange={(e) => handleEditFieldChange(record.id, 'lastname', e.target.value)}
                                                                    placeholder="Enter last name"
                                                                    error={editModalType === 'emptyLastName' && !editingRecords[record.id]?.lastname}
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            bgcolor: editModalType === 'emptyLastName' ? alpha(theme.palette.warning.main, 0.1) : 'transparent'
                                                                        }
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    type="email"
                                                                    value={editingRecords[record.id]?.email || ''}
                                                                    onChange={(e) => handleEditFieldChange(record.id, 'email', e.target.value)}
                                                                    placeholder="Enter email"
                                                                    error={editModalType === 'emptyEmail' && !editingRecords[record.id]?.email}
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            bgcolor: editModalType === 'emptyEmail' ? alpha(theme.palette.error.main, 0.1) : 'transparent'
                                                                        }
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    value={editingRecords[record.id]?.phone || ''}
                                                                    onChange={(e) => handleEditFieldChange(record.id, 'phone', e.target.value)}
                                                                    placeholder="+447xxxxxxxxx"
                                                                    error={editModalType === 'invalidPhone' && !editingRecords[record.id]?.phone}
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            bgcolor: editModalType === 'invalidPhone' ? alpha(theme.palette.error.main, 0.1) : 'transparent'
                                                                        }
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Tooltip title="Remove from import">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleRemoveRecord(record)}
                                                                    >
                                                                        <Iconify icon="mdi:delete" width={18} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </DialogContent>
                                    <DialogActions sx={{ p: 2 }}>
                                        <Button onClick={handleCloseEditModal} color="inherit">
                                            Cancel
                                        </Button>
                                        <Button onClick={handleSaveAllEdits} variant="contained" color="primary">
                                            Save All Changes
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </Box>
                        )}
                        {/** Activty Step preview */}
                        {activeStep === 4 && (<Box sx={styles.dropBox}>
                            <Tooltip title="Preview CSV File">
                                <Button href={originalDataFile} sx={{ ml: 1 }} endIcon={<Iconify
                                    sx={{ cursor: 'pointer' }}
                                    icon={'material-symbols:download-rounded'}
                                    width={14}
                                    height={14}
                                />}>
                                    <Typography sx={{ fontSize: '14px', color: grey[600] }}>Click here to preview final csv file</Typography>
                                </Button>
                            </Tooltip>

                            {invalidCsv ? (<Tooltip title="Preview Invalidated Records">
                                <Button href={invalidCsv} sx={{ ml: 1 }} endIcon={<Iconify
                                    sx={{ cursor: 'pointer' }}
                                    icon={'material-symbols:download-rounded'}
                                    width={14}
                                    height={14}
                                />}>
                                    <Typography sx={{ fontSize: '14px', color: grey[600] }}>Click here to preview the invalidated email / phone records</Typography>
                                </Button>
                            </Tooltip>) : null}

                        </Box>)}
                    </Box>
                    <Box sx={styles.footer}>
                        <Button
                            sx={{ height: "2rem" }}
                            variant="outlined"
                            disabled={activeStep === 0}
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                        <Button sx={{ height: "2rem" }}
                            variant="outlined"
                            disabled={activeStep === (STEPS.length - 1) || isNextDisabled(activeStep)}
                            onClick={handleNext}
                        >Next</Button>
                    </Box>
                </Card>
            </Box>
        </>
    )
};

UploadFileV2.propTypes = {
    setDisableSave: PropTypes.func
}

export default UploadFileV2;
